/**
 * Manage permissions for one user: grant and revoke (aligned with Users & permissions breakdown).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import adminApi from '../../services/adminApi';
import type {
  Country,
  DataPermission,
  PermissionLevel,
  UserWithPermissionsItem,
  ValueChain,
} from '../../types';

function normalizeUser(u: UserWithPermissionsItem): UserWithPermissionsItem {
  const anyU = u as unknown as Record<string, unknown>;
  return {
    ...u,
    value_chain_access:
      u.value_chain_access ??
      (anyU.valueChainAccess as UserWithPermissionsItem['value_chain_access']) ??
      [],
    country_access:
      u.country_access ??
      (anyU.countryAccess as UserWithPermissionsItem['country_access']) ??
      [],
    data_permissions:
      u.data_permissions ??
      (anyU.dataPermissions as UserWithPermissionsItem['data_permissions']) ??
      [],
    financial_data_access:
      u.financial_data_access ??
      (anyU.financialDataAccess as UserWithPermissionsItem['financial_data_access']) ??
      [],
  };
}

function partitionDataPermissions(data: DataPermission[] | undefined): {
  prodcom: DataPermission[];
  graphSource: DataPermission[];
} {
  const list = data ?? [];
  const prodcom: DataPermission[] = [];
  const graphSource: DataPermission[] = [];
  for (const p of list) {
    if (p.data_type === 'prodcom_view') prodcom.push(p);
    else if (p.data_type === 'graph_source') graphSource.push(p);
  }
  return { prodcom, graphSource };
}

function errMessage(e: unknown): string {
  const any = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const d = any?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => String(x)).join(', ');
  return any?.message || 'Request failed';
}

export interface UserPermissionRevokeDialogProps {
  open: boolean;
  user: UserWithPermissionsItem | null;
  onClose: () => void;
  /** Called after any successful grant or revoke so the parent can refetch the list. */
  onPermissionsChanged: () => void;
}

type GraphSourceRow = { source_db: string; label: string; count: number };
type FinancialRow = { country: string; source: string; label: string };

const LEVELS: PermissionLevel[] = ['read', 'write', 'admin'];

const UserPermissionRevokeDialog: React.FC<UserPermissionRevokeDialogProps> = ({
  open,
  user,
  onClose,
  onPermissionsChanged,
}) => {
  const [detail, setDetail] = useState<UserWithPermissionsItem | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [catalogsError, setCatalogsError] = useState<string | null>(null);
  const [valueChains, setValueChains] = useState<ValueChain[]>([]);
  const [graphSourcesList, setGraphSourcesList] = useState<GraphSourceRow[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [financialSources, setFinancialSources] = useState<FinancialRow[]>([]);

  const [vcPick, setVcPick] = useState<ValueChain | null>(null);
  const [vcLevel, setVcLevel] = useState<PermissionLevel>('read');
  const [gsPick, setGsPick] = useState<GraphSourceRow | null>(null);
  const [gsLevel, setGsLevel] = useState<PermissionLevel>('read');
  const [countryPick, setCountryPick] = useState<Country | null>(null);
  const [countryLevel, setCountryLevel] = useState<PermissionLevel>('read');
  const [finPick, setFinPick] = useState<FinancialRow | null>(null);
  const [finLevel, setFinLevel] = useState<PermissionLevel>('read');

  useEffect(() => {
    if (open && user) {
      setDetail(normalizeUser(user));
      setError(null);
      setCatalogsError(null);
    } else if (!open) {
      setDetail(null);
      setVcPick(null);
      setGsPick(null);
      setCountryPick(null);
      setFinPick(null);
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      setCatalogsLoading(true);
      setCatalogsError(null);
      try {
        const [vc, gs, co, finRes] = await Promise.all([
          adminApi.getValueChains(),
          adminApi.getGraphSources(false),
          adminApi.getCountries(),
          adminApi.getFinancialSources(),
        ]);
        if (cancelled) return;
        setValueChains(Array.isArray(vc) ? vc : []);
        setGraphSourcesList(Array.isArray(gs) ? gs : []);
        setCountries(Array.isArray(co) ? co : []);
        setFinancialSources(finRes?.data && Array.isArray(finRes.data) ? finRes.data : []);
      } catch (e) {
        if (!cancelled) setCatalogsError(errMessage(e));
      } finally {
        if (!cancelled) setCatalogsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  const refreshFromServer = useCallback(async (userId: number) => {
    const u = await adminApi.getUserPermissions(userId);
    setDetail(normalizeUser(u));
  }, []);

  const { prodcom, graphSource } = partitionDataPermissions(detail?.data_permissions);

  /** Backend revokes are soft (is_active=false); UI shows only effective (active) grants. */
  const activeValueChains = useMemo(
    () => detail?.value_chain_access.filter((a) => a.is_active) ?? [],
    [detail]
  );
  const activeGraphSources = useMemo(() => graphSource.filter((p) => p.is_active), [graphSource]);
  const activeProdcom = useMemo(() => prodcom.filter((p) => p.is_active), [prodcom]);
  const activeCountries = useMemo(
    () => detail?.country_access.filter((a) => a.is_active) ?? [],
    [detail]
  );
  const activeFinancial = useMemo(
    () => detail?.financial_data_access.filter((f) => f.is_active) ?? [],
    [detail]
  );

  const availableVC = useMemo(() => {
    if (!detail) return [];
    const granted = new Set(
      detail.value_chain_access.filter((a) => a.is_active).map((a) => a.value_chain_id)
    );
    return valueChains.filter((v) => v.element_id && !granted.has(v.element_id));
  }, [detail, valueChains]);

  const availableGS = useMemo(() => {
    if (!detail) return [];
    const granted = new Set(
      graphSource.filter((p) => p.is_active).map((p) => p.data_identifier)
    );
    return graphSourcesList.filter((g) => g.source_db && !granted.has(g.source_db));
  }, [detail, graphSourcesList, graphSource]);

  const availableCountries = useMemo(() => {
    if (!detail) return [];
    return countries.filter(
      (c) =>
        !detail.country_access.some(
          (a) =>
            a.is_active && (a.country_code === c.code || a.country_name === c.name)
        )
    );
  }, [detail, countries]);

  const availableFinancial = useMemo(() => {
    if (!detail) return [];
    return financialSources.filter(
      (fs) =>
        !detail.financial_data_access.some(
          (f) => f.is_active && f.country === fs.country && f.source === fs.source
        )
    );
  }, [detail, financialSources]);

  const showGrantProdcom =
    detail && !prodcom.some((p) => p.is_active && p.data_type === 'prodcom_view');

  const handleRemove = async (
    permissionId: number,
    permissionType: 'value_chain' | 'country' | 'data' | 'financial'
  ) => {
    if (!detail) return;
    const key = `${permissionType}-${permissionId}`;
    setBusyKey(key);
    setError(null);
    try {
      switch (permissionType) {
        case 'value_chain':
          await adminApi.revokeValueChainPermission(permissionId);
          break;
        case 'country':
          await adminApi.revokeCountryPermission(permissionId);
          break;
        case 'data':
          await adminApi.revokeDataPermission(permissionId);
          break;
        case 'financial':
          await adminApi.revokeFinancialDataPermission(permissionId);
          break;
        default:
          throw new Error('Invalid permission type');
      }
      await refreshFromServer(detail.id);
      onPermissionsChanged();
    } catch (e) {
      console.error(e);
      setError(errMessage(e));
    } finally {
      setBusyKey(null);
    }
  };

  const runGrant = async (key: string, fn: () => Promise<unknown>) => {
    if (!detail) return;
    setBusyKey(key);
    setError(null);
    try {
      await fn();
      await refreshFromServer(detail.id);
      onPermissionsChanged();
    } catch (e) {
      console.error(e);
      setError(errMessage(e));
    } finally {
      setBusyKey(null);
    }
  };

  const grantValueChain = () => {
    if (!detail || !vcPick) return;
    runGrant('grant-vc', () =>
      adminApi.grantValueChainPermission({
        user_id: detail.id,
        value_chain_id: vcPick.element_id,
        value_chain_name: vcPick.name,
        permission_level: vcLevel,
      }).then(() => {
        setVcPick(null);
      })
    );
  };

  const grantGraphSource = () => {
    if (!detail || !gsPick) return;
    runGrant('grant-gs', () =>
      adminApi.grantDataPermission({
        user_id: detail.id,
        data_type: 'graph_source',
        data_identifier: gsPick.source_db,
        permission_level: gsLevel,
        metadata: null,
      }).then(() => {
        setGsPick(null);
      })
    );
  };

  const grantProdcom = () => {
    if (!detail) return;
    runGrant('grant-prodcom', () =>
      adminApi.grantDataPermission({
        user_id: detail.id,
        data_type: 'prodcom_view',
        data_identifier: 'enabled',
        permission_level: 'read',
        metadata: null,
        notes: 'Granted from Users & permissions',
      })
    );
  };

  const grantCountry = () => {
    if (!detail || !countryPick) return;
    runGrant('grant-country', () =>
      adminApi
        .grantCountryPermission({
          user_id: detail.id,
          country_code: countryPick.code,
          country_name: countryPick.name,
          permission_level: countryLevel,
        })
        .then(() => {
          setCountryPick(null);
        })
    );
  };

  const grantFinancial = () => {
    if (!detail || !finPick) return;
    runGrant('grant-fin', () =>
      adminApi
        .grantFinancialDataPermission({
          user_id: detail.id,
          country: finPick.country,
          source: finPick.source,
          permission_level: finLevel,
        })
        .then(() => {
          setFinPick(null);
        })
    );
  };

  const disabled = busyKey !== null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Manage permissions
        {detail && (
          <Typography variant="body2" color="text.secondary" component="span" display="block">
            {detail.full_name} · {detail.email}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {catalogsLoading && (
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading resource lists…
            </Typography>
          </Box>
        )}
        {catalogsError && (
          <Typography color="warning.main" variant="body2" sx={{ mb: 2 }}>
            Could not load all resource lists: {catalogsError}. Granting may be limited.
          </Typography>
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {!detail ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Value chains
              </Typography>
              {activeValueChains.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {activeValueChains.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.value_chain_name} · ${p.permission_level}`}
                      variant="filled"
                      onDelete={() => handleRemove(p.id, 'value_chain')}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, flex: 1 }}
                  options={availableVC}
                  value={vcPick}
                  onChange={(_, v) => setVcPick(v)}
                  getOptionLabel={(o) => o.name || o.element_id}
                  disabled={disabled || catalogsLoading}
                  renderInput={(params) => <TextField {...params} label="Add value chain" />}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={vcLevel}
                    onChange={(e) => setVcLevel(e.target.value as PermissionLevel)}
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={disabled || !vcPick} onClick={grantValueChain}>
                  Add
                </Button>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Graph sources
              </Typography>
              {activeGraphSources.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {activeGraphSources.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.data_identifier} · ${p.permission_level}`}
                      variant="filled"
                      onDelete={() => handleRemove(p.id, 'data')}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, flex: 1 }}
                  options={availableGS}
                  value={gsPick}
                  onChange={(_, v) => setGsPick(v)}
                  getOptionLabel={(o) => o.label || o.source_db}
                  disabled={disabled || catalogsLoading}
                  renderInput={(params) => <TextField {...params} label="Add graph source" />}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={gsLevel}
                    onChange={(e) => setGsLevel(e.target.value as PermissionLevel)}
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={disabled || !gsPick} onClick={grantGraphSource}>
                  Add
                </Button>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                PRODCOM
              </Typography>
              {activeProdcom.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {activeProdcom.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`PRODCOM (${p.permission_level})`}
                      variant="filled"
                      onDelete={() => handleRemove(p.id, 'data')}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              )}
              {showGrantProdcom && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={disabled}
                  onClick={grantProdcom}
                  sx={{ mt: 1 }}
                >
                  Grant PRODCOM view
                </Button>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Countries
              </Typography>
              {activeCountries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {activeCountries.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.country_name} · ${p.permission_level}`}
                      variant="filled"
                      onDelete={() => handleRemove(p.id, 'country')}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, flex: 1 }}
                  options={availableCountries}
                  value={countryPick}
                  onChange={(_, v) => setCountryPick(v)}
                  getOptionLabel={(o) => (o.name ? `${o.name} (${o.code})` : o.code)}
                  disabled={disabled || catalogsLoading}
                  renderInput={(params) => <TextField {...params} label="Add country" />}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={countryLevel}
                    onChange={(e) => setCountryLevel(e.target.value as PermissionLevel)}
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={disabled || !countryPick} onClick={grantCountry}>
                  Add
                </Button>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Financial
              </Typography>
              {activeFinancial.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {activeFinancial.map((p) => (
                    <Chip
                      key={p.id}
                      size="small"
                      label={`${p.country} · ${p.source} · ${p.permission_level}`}
                      variant="filled"
                      onDelete={() => handleRemove(p.id, 'financial')}
                      disabled={disabled}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 260, flex: 1 }}
                  options={availableFinancial}
                  value={finPick}
                  onChange={(_, v) => setFinPick(v)}
                  getOptionLabel={(o) => o.label || `${o.country} / ${o.source}`}
                  disabled={disabled || catalogsLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="Add financial country / source" />
                  )}
                />
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    label="Level"
                    value={finLevel}
                    onChange={(e) => setFinLevel(e.target.value as PermissionLevel)}
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" disabled={disabled || !finPick} onClick={grantFinancial}>
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserPermissionRevokeDialog;
